// Single source of truth for the price labels that show up in marketing
// copy across the site (home FAQ, mid-page CTA, comparison pages, blog
// articles, "what is PhantomCodeAI", etc.). Centralised here so a price
// bump in PLANS only has to be reflected in one set of strings.
//
// USD figures match `DISPLAY_PLAN_PRICES.<plan>.USD.amount` in
// `lib/config/plans.ts`. INR figures match `PLANS.<plan>.price`.
//
// Why a separate module from plans.ts:
//   * plans.ts owns the runtime checkout numbers (server-side).
//   * marketing-prices.ts owns the human-facing strings that appear in
//     copy. Two-line strings ("₹4,299/month", "$49/mo") need different
//     formatting from the integer prices used at checkout, and we want a
//     compact, predictable set of keys for marketing components to import.

import type { PricingCurrency } from "./plans"

export interface MarketingPriceLabels {
  /** "Starter" credit pack — entry sticker price. */
  starter: string
  /** "Basic" credit pack. */
  basic: string
  /** "Power" credit pack. */
  power: string
  /** Monthly subscription, with "/month" suffix. */
  monthly: string
  /** Weekly subscription, with "/week" suffix. */
  weekly: string
  /** Yearly subscription, with "/year" suffix. */
  yearly: string

  // Convenience phrases that appear repeatedly across copy. Defined here
  // so individual pages don't reinvent them and accidentally drift.
  /** "₹1,599 once" / "$19 once" — for "Start with a Starter pack" CTAs. */
  starterOnce: string
  /**
   * "₹1,599" / "$19" — for "Plans start at …" / "Starting at …" copy across
   * the marketing site. Always the lowest sticker price we offer (Starter
   * credit pack), with NO "/month" suffix — the Starter pack is one-time, so
   * tagging it with a per-month cadence would be misleading. If a page wants
   * to advertise the monthly subscription specifically, use `monthly` instead.
   */
  startingTagline: string
  /** Compact range used in hero/CTA blurbs ("₹1,599–₹19,999" / "$19–$199"). */
  rangeBlurb: string
  /** "INR" / "USD" string for headings or schema. */
  currencyName: string
  /** "₹" / "$" — bare currency symbol when only the symbol is needed. */
  symbol: string
}

const LABELS: Record<PricingCurrency, MarketingPriceLabels> = {
  INR: {
    starter: "₹1,599",
    basic: "₹2,599",
    power: "₹3,599",
    weekly: "₹2,499/week",
    monthly: "₹4,299/month",
    yearly: "₹19,999/year",
    starterOnce: "₹1,599 once",
    startingTagline: "₹1,599",
    rangeBlurb: "₹1,599–₹19,999",
    currencyName: "INR",
    symbol: "₹",
  },
  USD: {
    starter: "$19",
    basic: "$29",
    power: "$39",
    weekly: "$29/week",
    monthly: "$49/month",
    yearly: "$199/year",
    starterOnce: "$19 once",
    startingTagline: "$19",
    rangeBlurb: "$19–$199",
    currencyName: "USD",
    symbol: "$",
  },
}

export function getMarketingPriceLabels(currency: PricingCurrency): MarketingPriceLabels {
  return LABELS[currency]
}
